import { useState, type FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      form.reset();
      return;
    }
    formData.delete("phone_alt");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      const response = await fetch(form.action, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (
        response.ok &&
        parsed &&
        parsed.code === "OK"
      ) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后重试");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查连接后重试");
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: "关于我们" },
            ]}
          />

          <section className="mb-16">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              关于爱情岛论坛
            </h1>
            <div className="h-1 w-16 bg-primary-500 rounded-full mb-8"></div>

            <div className="rounded-2xl overflow-hidden mb-10 h-64 md:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=A%20warm%20and%20welcoming%20digital%20community%20hub%20illustration%2C%20soft%20coral%20and%20peach%20gradient%20background%2C%20people%20connecting%20through%20floating%20message%20bubbles%2C%20modern%20flat%20illustration%20style%2C%20friendly%20and%20inclusive%20atmosphere%2C%20Chinese%20community%20website%20aesthetic%2C%20clean%20and%20harmonious%20composition&width=1200&height=500&seq=about-hero-2026&orientation=landscape"
                alt="关于爱情岛论坛"
                title="爱情岛论坛 - 关于我们"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="space-y-5">
              <h2 className="font-heading text-xl font-bold text-foreground-900">品牌故事</h2>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                爱情岛论坛创立于2020年，是一个致力于打造温暖、高品质中文社区交流平台的互联网项目。创始团队由一群热爱互联网、坚信社区力量的年轻人组成，他们希望在这个信息爆炸的时代，为大家提供一个真正有温度、有深度的交流空间。
              </p>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                "爱情岛"这个名字承载着创始团队的美好愿景——我们希望这个社区像一个温暖的小岛，让每一个来到这里的人都能感受到归属感和安全感。在这里，你可以自由分享生活中的点滴感悟，倾听他人的故事，找到志同道合的朋友，共同成长。
              </p>
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                经过多年的发展，爱情岛论坛已经从最初的小型讨论社区，成长为拥有数百万注册用户的大型综合交流平台。我们的用户覆盖全国各个省市，涵盖了不同年龄层和职业背景。论坛内容也从最初的单一情感交流板块，扩展到如今涵盖生活、技术、娱乐、美食、健康等多个领域的综合社区。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">核心价值</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  icon: "ri-heart-3-line",
                  title: "温暖友善",
                  desc: "我们倡导温暖友善的交流氛围，每一位用户的声音都值得被倾听和尊重。",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "安全可靠",
                  desc: "严格的内容审核机制和隐私保护体系，确保你的每一次访问都安心无忧。",
                },
                {
                  icon: "ri-star-line",
                  title: "品质内容",
                  desc: "鼓励原创和深度分享，拒绝低质信息和恶意灌水，维护高品质内容生态。",
                },
                {
                  icon: "ri-group-2-line",
                  title: "共建共享",
                  desc: "社区的建设离不开每一位用户的参与，我们一起让爱情岛变得更加美好。",
                },
              ].map((item) => (
                <div key={item.title} className="bg-background-100 rounded-xl p-6 flex gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-primary-500 text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1.5">{item.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="reputation" className="mb-16">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  name: "小雨",
                  role: "情感交流板块活跃用户",
                  text: "在爱情岛论坛找到了很多温暖的朋友，每次心情低落的时候来这里看看大家的分享，总会被治愈。",
                  rating: 5,
                },
                {
                  name: "技术宅阿明",
                  role: "技术讨论板块版主",
                  text: "技术板块的氛围真的很好，大家乐于分享也愿意帮助新人，是我见过最友善的技术社区。",
                  rating: 5,
                },
                {
                  name: "旅行达人Lily",
                  role: "美食旅游板块达人",
                  text: "通过论坛认识了一群志同道合的旅行伙伴，已经一起走过了10多个城市！感谢爱情岛。",
                  rating: 5,
                },
                {
                  name: "职场新人小王",
                  role: "注册2年的老用户",
                  text: "从大学生到职场新人，爱情岛见证了我的成长。这里的每一篇分享都让我受益匪浅。",
                  rating: 5,
                },
              ].map((review, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 leading-relaxed text-sm mb-4">{review.text}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-user-line text-primary-500"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{review.name}</div>
                      <div className="text-xs text-foreground-400">{review.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">联系我们</h2>
            <div className="bg-background-100 rounded-2xl p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-500">contact@aiqingdao.net</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1">客服热线</h3>
                    <p className="text-sm text-foreground-500">400-888-0000</p>
                    <p className="text-xs text-foreground-400">周一至周日 9:00 - 21:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-500">爱情岛社区</p>
                    <p className="text-xs text-foreground-400">搜索公众号关注获取最新动态</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-weibo-line text-primary-500 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1">官方微博</h3>
                    <p className="text-sm text-foreground-500">@爱情岛论坛官方</p>
                    <p className="text-xs text-foreground-400">关注微博参与社区话题互动</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-background-200/60 pt-8">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4">在线留言</h3>

                {formStatus === "success" && (
                  <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-center gap-3">
                    <i className="ri-checkbox-circle-line text-emerald-600 text-lg"></i>
                    <span className="text-sm text-emerald-700">留言已成功发送，我们会尽快回复你！</span>
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="mb-4 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 flex items-center gap-3">
                    <i className="ri-error-warning-line text-rose-600 text-lg"></i>
                    <span className="text-sm text-rose-700">{formError || "提交失败，请稍后重试"}</span>
                  </div>
                )}

                <form
                  id="contact-form"
                  action="https://readdy.ai/api/form/d9pn8cmb4qjhggaeq1lg"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                  data-readdy-form=""
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="name"
                      placeholder="你的姓名"
                      className="bg-background-50 border border-background-200/60 rounded-lg px-4 py-3 text-sm text-foreground-700 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="邮箱地址"
                      className="bg-background-50 border border-background-200/60 rounded-lg px-4 py-3 text-sm text-foreground-700 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      required
                    />
                  </div>
                  <textarea
                    name="message"
                    rows={4}
                    placeholder="请描述你的问题或建议..."
                    maxLength={500}
                    className="bg-background-50 border border-background-200/60 rounded-lg px-4 py-3 text-sm text-foreground-700 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                    required
                  ></textarea>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="self-start bg-primary-500 hover:bg-primary-600 text-background-50 font-medium px-8 py-3 rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-send-plane-line mr-2"></i>发送留言
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}